import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { PLACEHOLDER_IMAGE_AVATAR, EEvolveLandingPageTabs } from '@configs'
import { Card, Empty, Skeleton, TableProps, Tooltip, message } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, SharedTable } from 'src/common'
import ConfirmDeleteModal from 'src/components/staff-and-role-management/ConfirmDeleteModal'
import { IPhotoMedia } from 'src/interfaces'
import { useAppDispatch, useAppSelector } from '@redux'
import {
  getEvolveFeatureListAction,
  createEvolveFeatureAction,
  updateEvolveFeatureAction,
  deleteEvolveFeatureAction,
} from 'src/redux/actions/evolve-landing-management'
import {
  selectEvolveFeatures,
  selectEvolveFeatureListLoading,
  selectEvolveFeatureCreateLoading,
  selectEvolveFeatureUpdateLoading,
  selectEvolveFeatureDeleteLoading,
} from 'src/redux/Slices/evolveLanding-management'
import {
  FeatureDetailModal,
  IAddFeatureRequest,
  IFeatureDetail,
} from './FeatureDetailModal'

type Props = { activeTab?: string }

const FeatureTab = ({ activeTab }: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation(['error', 'common','evolveLanding'])

  // Local state
  const [showModal, setShowModal] = useState(false)
  const [isModalLoading, setIsModalLoading] = useState(false)
  const [selectedFeature, setSelectedFeature] = useState<IFeatureDetail | null>(
    null
  )
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false)
  const [selectedFeatureId, setSelectedFeatureId] = useState<number | null>(
    null
  )

  // Redux selectors
  const features = useAppSelector(selectEvolveFeatures)
  const isGetLoading = useAppSelector(selectEvolveFeatureListLoading)
  const isCreateLoading = useAppSelector(selectEvolveFeatureCreateLoading)
  const isUpdateLoading = useAppSelector(selectEvolveFeatureUpdateLoading)
  const isDeleteLoading = useAppSelector(selectEvolveFeatureDeleteLoading)

  const isActionLoading = isCreateLoading || isUpdateLoading

  // Modal handlers
  const onCloseConfirmModal = () => {
    setOpenConfirmDeleteModal(false)
    setSelectedFeatureId(null)
  }

  const onClose = () => {
    setShowModal(false)
    setSelectedFeature(null)
  }

  const onOpen = () => {
    setSelectedFeature(null)
    setShowModal(true)
  }

  // Table configuration
  const columns: TableProps<IFeatureDetail>['columns'] = [
    {
      dataIndex: 'no',
      align: 'center',
      key: 'no',
      width: '8%',
      title: 'No',
    },
    {
      dataIndex: 'media',
      align: 'center',
      key: 'media',
      width: '12%',
      title: 'Image',
      render: (record: IPhotoMedia) => (
        <div className="flex items-center justify-center w-full">
          <img
            src={record?.url ?? PLACEHOLDER_IMAGE_AVATAR}
            alt="Feature"
            className="object-cover w-14 h-14 rounded"
          />
        </div>
      ),
    },
    {
      dataIndex: 'mediaMobile',
      align: 'center',
      key: 'mediaMobile',
      width: '12%',
      title: 'Image Mobile',
      render: (record: IPhotoMedia) => (
        <div className="flex items-center justify-center w-full">
          <img
            src={record?.url ?? PLACEHOLDER_IMAGE_AVATAR}
            alt="Feature mobile"
            className="object-cover w-14 h-14 rounded"
          />
        </div>
      ),
    },
    {
      dataIndex: 'title',
      align: 'center',
      key: 'title',
      width: '15%',
      title: 'Title EN',
      ellipsis: true,
    },
    {
      dataIndex: 'titleInDutch',
      align: 'center',
      key: 'titleInDutch',
      width: '15%',
      title: 'Title NL',
      ellipsis: true,
    },
    {
      dataIndex: 'content',
      align: 'center',
      key: 'content',
      width: '18%',
      title: 'Content EN',
      ellipsis: true,
    },
    {
      dataIndex: 'contentInDutch',
      align: 'center',
      key: 'contentInDutch',
      width: '18%',
      title: 'Content NL',
      ellipsis: true,
    },
    {
      dataIndex: 'action',
      align: 'center',
      key: 'action',
      width: '12%',
      title: 'Action',
      render: (_, record: IFeatureDetail) => (
        <div className="flex space-x-4 items-center justify-center w-full">
          <Tooltip title={t('common:edit')}>
            <EditOutlined
              className="text-lg font-light cursor-pointer text-[#184f64] hover:opacity-75"
              onClick={() => {
                setSelectedFeature(record)
                setShowModal(true)
              }}
            />
          </Tooltip>
          <Tooltip title={t('common:delete')}>
            <DeleteOutlined
              className="text-lg font-light cursor-pointer !text-red-600 hover:opacity-75"
              onClick={() => {
                setSelectedFeatureId(record.id)
                setOpenConfirmDeleteModal(true)
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ]

  // CRUD operations
  const onDelete = async () => {
    if (!selectedFeatureId) return

    try {
      const response = await dispatch(
        deleteEvolveFeatureAction(selectedFeatureId)
      ).unwrap()

      if (response?.success) {
        message.success('Feature deleted successfully')
        onCloseConfirmModal()
        dispatch(getEvolveFeatureListAction())
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete feature'
      message.error(errorMessage)
    }
  }

  const onSave = async (data: IAddFeatureRequest) => {
    try {
      if (selectedFeature) {
        // Update existing feature
        const response = await dispatch(
          updateEvolveFeatureAction({
            id: selectedFeature.id,
            params: data,
          })
        ).unwrap()

        if (response) {
          message.success('Feature updated successfully')
          onClose()
          // Force a re-fetch to ensure the list is up to date
          dispatch(getEvolveFeatureListAction())
        }
      } else {
        // Create new feature
        const response = await dispatch(
          createEvolveFeatureAction(data)
        ).unwrap()

        if (response) {
          message.success('Feature created successfully')
          onClose()
          // Force a re-fetch to ensure the list is up to date
          dispatch(getEvolveFeatureListAction())
        }
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to save feature'
      message.error(errorMessage)
    }
  }

  // Fetch features when tab becomes active
  useEffect(() => {
    if (activeTab === EEvolveLandingPageTabs.FEATURES_LOVE) {
      dispatch(getEvolveFeatureListAction())
    }
  }, [activeTab, dispatch])

  return (
    <Card>
      <div className="flex justify-end mb-4">
        <Button
          type="primary"
          size="middle"
          onClick={onOpen}
          disabled={isActionLoading}
        >
          Add New Feature
        </Button>
      </div>

      {isGetLoading ? (
        <Skeleton paragraph={{ rows: 4 }} />
      ) : features?.length === 0 ? (
        <Empty description={t('evolveLanding:no_data')} />
      ) : (
        <SharedTable
          columns={columns}
          dataSource={(() => {
            if (!features || !Array.isArray(features)) {
              return []
            }

            return features.map((item: IFeatureDetail) => ({
              ...item,
              key: item.id,
              title: item.title || `-${t('error:no_data')}-`,
              titleInDutch: item.titleInDutch || `-${t('error:no_data')}-`,
              content: item.content || `-${t('error:no_data')}-`,
              contentInDutch: item.contentInDutch || `-${t('error:no_data')}-`,
            }))
          })()}
          sticky
          hasIndexColumn={false}
        />
      )}

      {showModal && (
        <FeatureDetailModal
          open={showModal}
          onClose={onClose}
          isLoading={isActionLoading || isModalLoading}
          changeLoading={setIsModalLoading}
          onSave={onSave}
          item={selectedFeature}
        />
      )}

      {openConfirmDeleteModal && (
        <ConfirmDeleteModal
          title="Delete Feature"
          content="Are you sure you want to delete this feature? This action cannot be undone."
          open={openConfirmDeleteModal}
          isLoading={!!isDeleteLoading}
          onDelete={onDelete}
          onClose={onCloseConfirmModal}
        />
      )}
    </Card>
  )
}

export default FeatureTab
